"use client"

import { useEffect, useRef, useState } from "react"

import { useTranslations } from "next-intl"

import { Button } from "@/components/ui"
import { EmptyState } from "@/components/ui/empty-state"
import { notifyHostDeclared, screenHostProgress } from "@/platform/app-mode/screen-visibility"
import { HOST_STALL_MS } from "@/platform/app-mode/stall-fallback"
import { useScreenAwaitingHost, useScreenProbing } from "@/platform/app-mode/use-screen-visible"
import { datadog } from "@/platform/observability/datadog/facade"

export const HostedStallRecovery = () => {
  const t = useTranslations("common")
  const awaiting = useScreenAwaitingHost()
  const probing = useScreenProbing()
  const [stalled, setStalled] = useState(false)
  const remaining = useRef(HOST_STALL_MS)

  useEffect(() => {
    document.documentElement.setAttribute("data-hosted-live", "")
    window.addEventListener("nelo:bridge-host-ready", notifyHostDeclared)
    return () => window.removeEventListener("nelo:bridge-host-ready", notifyHostDeclared)
  }, [])

  useEffect(() => {
    if (!awaiting) {
      remaining.current = HOST_STALL_MS
      setStalled(false)
      return
    }
    if (probing || stalled) return

    const startedAt = performance.now()
    const timer = setTimeout(() => {
      datadog.addAction("hosted_stall.shown", { progress: screenHostProgress() })
      setStalled(true)
    }, remaining.current)
    return () => {
      clearTimeout(timer)
      remaining.current = Math.max(0, remaining.current - (performance.now() - startedAt))
    }
  }, [awaiting, probing, stalled])

  if (!stalled) return null
  return (
    <div role="alert" className="bg-palette-canvas fixed inset-0 z-50 flex items-center justify-center">
      <EmptyState title={t("loadErrorEyebrow")} action={<Button onClick={() => window.location.reload()}>{t("retry")}</Button>}>
        {t("loadErrorBody")}
      </EmptyState>
    </div>
  )
}
